import { Checkbox } from '@shared';

import { t } from '@/i18n';
import { isAllChecked, toggleAll, toggleOne, type ConsentChecklistItem } from '@/entities/terms';

/**
 * TRAD-3759(W4) — 동의 목록(가입 (가) · 재동의 /terms-agreement (나) 공용). iOS·android 기준 화면.
 * 시안: Artifact Design 캔버스 «Consent row mockup (TRAD-3759)» — 행 4상태(기본 · 새 버전 · 문서 없음 · 모르는 종류) + 선택 항목.
 *
 * UI 룰 1 사유: 기존 TermsAgreement 는 «문서 하나의 본문 + 체크 하나»라 여러 항목·문서 없는 항목(만 14세)을 담을 수 없었고,
 * SignUp 의 체크는 문장 하나에 링크를 섞은 형태라 항목별 체크·기록이 안 된다. 체크박스 자체는 공용 `Checkbox` 를 그대로 쓴다.
 *
 * «보기»는 새 탭으로 연다 — 가입 화면에서 같은 탭으로 나가면 입력한 이메일·비밀번호가 사라진다.
 */
interface ConsentChecklistProps {
  items: readonly ConsentChecklistItem[];
  checked: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
  /** S3 «전체 동의» 줄. 따로 기록하지 않는 편의 체크. */
  showAll?: boolean;
  disabled?: boolean;
}

const ConsentChecklist = ({ items, checked, onChange, showAll = false, disabled = false }: ConsentChecklistProps) => (
  <div className="flex flex-col overflow-hidden rounded-lg border border-border" data-testid="consent-checklist">
    {showAll && (
      <div className="flex items-center gap-3 border-b border-border bg-muted px-4 py-3.5">
        <Checkbox
          checked={isAllChecked(items, checked)}
          onChange={e => onChange(toggleAll(items, e.target.checked))}
          disabled={disabled}
          className="h-5 w-5"
          containerClassName="flex flex-1 items-center gap-3"
          labelClassName="text-sm font-semibold"
          label={t('consent.all')}
        />
      </div>
    )}
    {items.map(item => (
      <div key={item.key} className="flex items-start gap-3 border-b border-border px-4 py-3.5 last:border-b-0">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <Checkbox
            checked={checked.has(item.key)}
            onChange={() => onChange(toggleOne(checked, item.key))}
            disabled={disabled}
            className="mt-0.5 h-5 w-5"
            containerClassName="flex items-start gap-3"
            labelClassName="text-sm leading-relaxed"
            label={item.label}
          />
          {item.note && <p className="pl-8 text-xs leading-relaxed text-muted-foreground">{item.note}</p>}
          {item.description && <p className="pl-8 text-xs leading-relaxed text-muted-foreground">{item.description}</p>}
        </div>
        {item.href && (
          <a
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 px-1 py-0.5 text-xs text-muted-foreground underline hover:text-foreground"
          >
            {t('consent.view')}
          </a>
        )}
      </div>
    ))}
  </div>
);

export default ConsentChecklist;
